/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { loginCustomer, registerCustomer, getCities } from "../utils/storeApi";
import Swal from "sweetalert2";

export default function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  tenantId,
  theme = { primary: "#7400BF", secondary: "#9333EA" },
}) {
  const [mode, setMode] = useState("login"); // 'login' or 'register'
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    address: "",
    cityId: localStorage.getItem("selectedCityId") || "",
  });

  useEffect(() => {
    const loadCities = async () => {
      const result = await getCities();
      if (result.success) {
        setCities(result.data);
      }
    };
    if (isOpen) loadCities();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    const result = await loginCustomer({
      tenantId,
      email: formData.email,
      password: formData.password,
    });

    if (!result?.success) {
      setLoading(false);
      Swal.fire("فشل تسجيل الدخول", result?.error || "خطأ غير معروف", "error");
      return;
    }

    const fullCustomer = {
      customerId: result.customerId,
      token: result.token,
      name: result.name || "",
      email: result.email || "",
      phone: result.phone || "",
      address: result.address || "",
      cityId: result.cityId || null,
      cityName: result.city || "",
    };

    localStorage.setItem("storeCustomerId", result.customerId);
    localStorage.setItem("storeCustomer", JSON.stringify(fullCustomer));
    setLoading(false);
    onSuccess(fullCustomer);
    onClose();
  };

 const handleRegister = async (e) => {
    e.preventDefault();
    
    // التحقق من اختيار المدينة
    const selectedCity = cities.find((c) => Number(c.cityId) === Number(formData.cityId));
    if (!selectedCity) {
      Swal.fire("خطأ", "الرجاء اختيار المحافظة", "error");
      return;
    }

    setLoading(true);

    try {
      const result = await registerCustomer({
        tenantId,
        ...formData,
        cityName: selectedCity.cityName,
      });

      // التحقق من نجاح العملية
      if (result && result.success) {
        // في حال النجاح، نقوم بتسجيل الدخول تلقائياً بالبيانات الموجودة
        await handleLogin(); 
      } else {
        // في حال الفشل (مثل الإيميل مسجل مسبقاً)
        setLoading(false);
        const errorMessage = result?.error || "فشل إنشاء الحساب";
        
        Swal.fire({
          title: "تنبيه",
          text: errorMessage === "Email already registered." 
                ? "هذا البريد الإلكتروني مسجل بالفعل، يرجى تسجيل الدخول." 
                : errorMessage,
          icon: "warning",
          confirmButtonColor: theme.primary,
        });
      }
    } catch (error) {
      setLoading(false);
      Swal.fire("خطأ", "حدث خطأ أثناء الاتصال بالخادم", "error");
    }
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 640;

  return (
    <div style={{
      ...overlayStyle,
      alignItems: isMobile ? "stretch" : "center",
      padding: isMobile ? 0 : "15px"
    }}>
      <div style={{
        ...modalStyle,
        width: isMobile ? "100vw" : "100%",
        maxWidth: isMobile ? "100vw" : "460px",
        height: isMobile ? "100vh" : "auto",
        maxHeight: isMobile ? "100vh" : "90vh",
        borderRadius: isMobile ? 0 : "20px",
        padding: isMobile ? "30px 22px 24px" : "34px 24px 26px",
        overflowY: "auto"
      }}>
        {/* زر الإغلاق */}
        <button onClick={onClose} style={closeBtn}>×</button>

        <div style={headerBox(theme)}>
          <h2 style={{ color: theme.primary, margin: 0 }}>
            {mode === "login" ? "أهلاً بعودتك!" : "إنشاء حساب جديد"}
          </h2>
        </div>

        <form onSubmit={mode === "login" ? handleLogin : handleRegister} style={formStyle}>
          {mode === "register" && (
            <>
              <input name="name" onChange={handleChange} required placeholder="الاسم الكامل" style={inputStyle} />
              <input name="phone" onChange={handleChange} placeholder="رقم الهاتف" style={inputStyle} />
              <input name="address" onChange={handleChange} placeholder="العنوان" style={inputStyle} />
              <select name="cityId" value={formData.cityId} onChange={handleChange} required style={inputStyle}>
                <option value="">اختر المحافظة</option>
                {cities.map((c) => (
                  <option key={c.cityId} value={c.cityId}>{c.cityName}</option>
                ))}
              </select>
            </>
          )}
          
          <input name="email" type="email" onChange={handleChange} required placeholder="البريد الإلكتروني" style={inputStyle} />
          <input name="password" type="password" onChange={handleChange} required placeholder="كلمة المرور" style={inputStyle} />

          <button type="submit" style={mainBtn(theme)} disabled={loading}>
            {loading ? "جاري التحميل..." : mode === "login" ? "تسجيل الدخول" : "إنشاء الحساب"}
          </button>

          <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} style={linkBtn(theme)}>
            {mode === "login" ? "ليس لديك حساب؟ سجل الآن" : "لديك حساب بالفعل؟ سجل دخول"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ✅ STYLES */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200000,
  padding: "15px",
};

const modalStyle = {
  background: "white",
  borderRadius: "20px",
  padding: "40px 30px 30px",
  width: "100%",
  maxWidth: "420px",
  position: "relative",
  direction: "rtl", // تثبيت الاتجاه من اليمين لليسار
  boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
};

const closeBtn = {
  position: "absolute",
  top: "15px",
  right: "15px",
  width: "30px",
  height: "30px",
  fontSize: "24px",
  border: "none",
  background: "#f0f0f0",
  color: "#333",
  cursor: "pointer",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0",
};

const headerBox = (theme) => ({
  background: `linear-gradient(135deg, ${theme.primary}15, ${theme.secondary}15)`,
  borderRadius: "15px",
  padding: "20px",
  marginBottom: "25px",
  textAlign: "center",
});

const formStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const inputStyle = {
  padding: "12px",
  borderRadius: "10px",
  border: "1px solid #ddd",
  fontSize: "14px",
  outline: "none",
  textAlign: "right", // ضمان محاذاة النص لليمين داخل الحقول
};

const mainBtn = (theme) => ({
  background: theme.primary,
  color: "white",
  border: "none",
  padding: "14px",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "16px",
  marginTop: "10px",
});

const linkBtn = (theme) => ({
  background: "none",
  border: "none",
  color: theme.primary,
  cursor: "pointer",
  fontWeight: "600",
  fontSize: "14px",
  marginTop: "5px",
});
